import { ProcessMeasure } from '../../domain/process-mesure';
import { ProcessSamplingCondition } from '../../domain/process-sampling-condition';
import { Securite } from '../../domain/securite';
import { mapper } from '../../tools/mapping/mapper';
import { ProcessMeasureEntity, ProcessSamplingConditionEntity, SecuriteEntity } from '../entity';
import type { AppDB } from '../db';

export class ProcessMeasureRepository {
    constructor(private readonly database: AppDB) {}


    async getProcessMeasureEntityById(id: number) {
        return await this.database.processMeasures.where('Id').equals(id).first(async ze => {
            return ze;
        });
    }

    async getProcessMeasureEntityByCode(code: string) {
        return await this.database.processMeasures.where('Code').equals(code).first(async me => {
            return me;
        });
    }

    async getProcessMeasureByIdWithChildren(id: number) {
        return await this.database.processMeasures.where('Id').equals(id).first(async pme => {
            const pm = mapper.map(pme, ProcessMeasureEntity, ProcessMeasure);
            if (pme?.ProcessSamplingConditionId) {
                const psce = await this.database.getProcessSamplingConditionEntityById(pme.ProcessSamplingConditionId)
                const psc = mapper.map(psce, ProcessSamplingConditionEntity, ProcessSamplingCondition);
                pm.ProcessSamplingCondition = psc;
            }
            if (pme?.OperatorIds) {
                for (let i = 0; i < pme?.OperatorIds.length; i++) {
                    pm.ProcessOperatorList.push(await this.database.getOperatorByIdWithChildren(pme?.OperatorIds[i]));
                }
            }
            if (pme?.SecuriteId) {
                const se = await this.database.getSecuriteEntityById(pme.SecuriteId);
                const s = mapper.map(se, SecuriteEntity, Securite)
                pm.Securite = s;
            }

            return pm;
        });
    }

    async getProcessMeasureByCodeWithChildren(code: string) {
        return await this.database.processMeasures.where('Code').equals(code).first(async pme => {
            const pm = mapper.map(pme, ProcessMeasureEntity, ProcessMeasure);
            if (pme?.ProcessSamplingConditionId) {
                const psce = await this.database.getProcessSamplingConditionEntityById(pme.ProcessSamplingConditionId)
                const psc = mapper.map(psce, ProcessSamplingConditionEntity, ProcessSamplingCondition);
                pm.ProcessSamplingCondition = psc;
            }
            if (pme?.OperatorIds) {
                for (let i = 0; i < pme?.OperatorIds.length; i++) {
                    pm.ProcessOperatorList.push(await this.database.getOperatorByIdWithChildren(pme?.OperatorIds[i]));
                }
            }
            if (pme?.SecuriteId) {
                const se = await this.database.getSecuriteEntityById(pme.SecuriteId);
                const s = mapper.map(se, SecuriteEntity, Securite)
                pm.Securite = s;
            }
            return pm;
        })
    }

    async attachProcessMeasure(measure: ProcessMeasure, processId: number) {
        let measureId: number;
        const pme = mapper.map(measure, ProcessMeasure, ProcessMeasureEntity);

        measureId = await this.database.processMeasures.add(pme);
        await this.database.process.update(processId, {
            MeasureId: measureId
        });

        if (measure.ProcessSamplingCondition) {
            await this.database.attachProcessSamplingCondition(measure.ProcessSamplingCondition, measureId);
        }
        if (measure.ProcessOperatorList) {
            for (let i = 0; i < measure.ProcessOperatorList.length; i++) {
                await this.database.attachOperator(measure.ProcessOperatorList[i], measureId);
            }
        }
        if (measure.Securite) {
            await this.database.attachSecuriteToProcess(measure.Securite, measureId);
        }

        return measureId;
    }

    async clearProcessMeasureChildren(measure: ProcessMeasure) {

        await this.database.transaction('rw', [this.database.operators, this.database.processMeasures, this.database.plageHoraires, this.database.cassettes, this.database.processSamplingConditions, this.database.tasks, this.database.securites, this.database.pompes], async () => {
            const pme = await this.database.getProcessMeasureEntityByCode(measure.Code);
            if (!pme || !pme.OperatorIds || !pme.ProcessSamplingConditionId) {
                return;
            }
            await this.database.operators.where('Id').anyOf(pme?.OperatorIds).toArray(async oe => {
                for (let i = 0; i < oe.length; i++) {
                    if (oe && oe[i] && oe[i].Id !== undefined) {
                        for (let j = 0; oe[i].CassetteIds && j < oe[i].CassetteIds!.length; j++) {
                            const ce = await this.database.getCassetteEntityById(oe[i].CassetteIds![j])
                            for (let k = 0; ce?.PlageHoraireIds && k < ce.PlageHoraireIds.length; k++) {
                                const phe = await this.database.getPlageHoraireEntityById(ce?.PlageHoraireIds[k]!)
                                await this.database.plageHoraires.where('Id').equals(phe?.Id!).delete();
                            }
                            await this.database.cassettes.where('Id').equals(ce?.Id!).delete();
                            await this.database.cassettes.update(ce!, { PlageHoraireIds: [] });
                        }
                        await this.database.operators.where('Id').equals(oe[i].Id!).delete();
                        await this.database.operators.update(oe[i], { CassetteIds: [] });
                    }
                    await this.database.processMeasures.update(pme, { OperatorIds: [] });
                }
            })

            await this.database.processSamplingConditions.where('Id').equals(pme.ProcessSamplingConditionId).delete();
        })
    }

    async attachProcessMeasureNewChildByCode(measure: ProcessMeasure) {

        await this.database.transaction('rw', [this.database.operators, this.database.processMeasures, this.database.plageHoraires, this.database.cassettes, this.database.processSamplingConditions, this.database.securites, this.database.tasks, this.database.pompes], async () => {
            const pme = await this.database.getProcessMeasureEntityByCode(measure.Code);
            if (!pme || !pme.Id) {
                return;
            }

            mapper.mutate(measure, pme, ProcessMeasure, ProcessMeasureEntity);
            await this.database.processMeasures.update(pme.Id, pme);

            if (measure.ProcessSamplingCondition) {
                await this.database.attachProcessSamplingCondition(measure.ProcessSamplingCondition, pme?.Id);
            }
            if (measure.ProcessOperatorList) {
                for (let i = 0; i < measure.ProcessOperatorList.length; i++) {
                    measure.ProcessOperatorList[i].Position = i + 1;
                    await this.database.attachOperator(measure.ProcessOperatorList[i], pme?.Id);
                }
            }
            if (measure.Securite) {
                await this.database.attachSecuriteToProcess(measure.Securite, pme?.Id);
            }
        })
        return true;
    }

    async cancelledProcessMeasure(measure: ProcessMeasure, reason: string) {
        const pme = await this.database.getProcessMeasureEntityByCode(measure.Code);
        if (!pme || !pme.Id) {
            return;
        }

        await this.database.processMeasures.update(pme.Id, { IsCancelled: true, Observation: reason });
        return true;
    }
}
